/**
 * A CAIXA DE SAÍDA: os e-mails que o servidor guardou em vez de mandar.
 *
 * Só tem conteúdo no ambiente fechado da auditoria (`SERVICO_DE_EMAIL=caixa_de_saida`). Ali nenhum
 * e-mail sai para caixa nenhuma de verdade, e é por esta página que quem testa recebe os links de
 * criar e trocar a senha. **Os links são clicáveis**, e o resto do texto aparece como texto: o
 * corpo em HTML não é desenhado aqui, para a página não executar nada que viesse dentro dele.
 */
import { useEffect, useState, type JSX } from 'react'
import { ehSessaoTerminada, ErroDoServidor } from '../../api'
import {
  administracao, type EuDaAdministracao, type MensagemDaCaixa
} from '../../api-da-administracao'
import { BarraDaAdministracao } from '../Administracao'

/** O texto, com cada endereço de internet virando um link clicável. */
function comLinks(texto: string): (string | JSX.Element)[] {
  const partes = texto.split(/(https?:\/\/[^\s<>"']+)/g)
  return partes.map((p, i) =>
    /^https?:\/\//.test(p)
      ? <a key={i} href={p} target="_blank" rel="noreferrer">{p}</a>
      : p
  )
}

function quando(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'medium' })
}

export function CaixaDeSaida({
  eu,
  aoSair
}: {
  eu: EuDaAdministracao
  aoSair: () => void
}): JSX.Element {
  const [estado, setEstado] = useState<
    { o: 'carregando' } | { o: 'erro'; frase: string } | { o: 'pronto'; ativa: boolean; mensagens: MensagemDaCaixa[] }
  >({ o: 'carregando' })

  useEffect(() => {
    administracao
      .caixaDeSaida()
      .then((r) => setEstado({ o: 'pronto', ativa: r.ativa, mensagens: r.mensagens }))
      .catch((falha: unknown) => {
        if (ehSessaoTerminada(falha)) return aoSair()
        setEstado({
          o: 'erro',
          frase: falha instanceof ErroDoServidor ? falha.message : 'Não foi possível abrir a caixa de saída.'
        })
      })
  }, [aoSair])

  return (
    <>
      <BarraDaAdministracao eu={eu} aoSair={aoSair} />
      <main
        className="folha administracao caixa-de-saida"
        data-pronto={estado.o === 'pronto' ? 'sim' : estado.o === 'erro' ? 'erro' : 'nao'}
      >
        <p className="linha-form"><a href="/administracao">◀ Campos</a></p>
        <h1>Caixa de saída</h1>

        {estado.o === 'carregando' && <p className="recado aguarde">Carregando...</p>}
        {estado.o === 'erro' && <p className="recado" role="alert">{estado.frase}</p>}

        {estado.o === 'pronto' && !estado.ativa && (
          <p className="recado nada">
            Este servidor manda os e-mails de verdade, e não guarda nada aqui. A caixa de saída só
            existe no ambiente fechado de auditoria.
          </p>
        )}

        {estado.o === 'pronto' && estado.ativa && (
          <>
            <p className="explicacao">
              Nenhum e-mail deste ambiente sai para caixa de verdade. Cada um fica aqui, o mais novo
              primeiro, e os links funcionam como funcionariam no e-mail.
            </p>
            {estado.mensagens.length === 0 && (
              <p className="recado nada">Nenhum e-mail ainda.</p>
            )}
            <ul className="mensagens-da-caixa">
              {estado.mensagens.map((m) => (
                <li key={m.id}>
                  <span className="quem">
                    <strong>{m.assunto}</strong>
                  </span>
                  <span className="detalhe">para {m.para} · {quando(m.criado_em)}</span>
                  <pre className="corpo">{comLinks(m.corpo_texto)}</pre>
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    </>
  )
}
